/**
 * acceptPickerHandoff.js — THE picker handoff contract.
 *
 * Single source of truth for "did the user successfully hand media
 * off to the platform". Used by every file-input intake surface.
 *
 * THE CONTRACT (operator-locked May 22, 2026):
 *
 *   input.files.length > 0  ⇒  handoff accepted.
 *
 * The browser's choice of event name — change, input, cancel, or no
 * event at all — is IRRELEVANT. The browser and the platform share
 * exactly one truth signal: input.files.
 *
 * See /app/memory/PICKER_HANDOFF_CONTRACT.md for the full doctrine.
 *
 * ── Why a standalone function rather than a hook or class? ────────
 *
 * Because it must be impossible to "almost implement" the contract.
 * Any new file-input intake surface that wants the contract MUST
 * import this function. Code review and doctrine guards can grep
 * for `acceptPickerHandoff(` and assert it appears in every intake
 * surface. There is no second implementation, no override, no copy.
 *
 * ── Usage ─────────────────────────────────────────────────────────
 *
 *   import { acceptPickerHandoff, fingerprintFile } from
 *     "../../lib/acceptPickerHandoff";
 *
 *   // On EVERY signal — change, input, cancel, polling tick,
 *   // focus, pageshow, visibilitychange — pass the input element
 *   // plus the set of fingerprints already staged this picker
 *   // session. Returns the NEW files to hand to the next layer
 *   // and the updated fingerprint set.
 *   const { files, fingerprints } = acceptPickerHandoff(
 *     inputElement, stagedFingerprintsThisSession,
 *   );
 *   if (files.length > 0) handOff(files);
 *
 * The function is pure and synchronous. No state. No awaits. No
 * thrown exceptions. Safe to call from any event handler or timer.
 */

/**
 * Build a stable per-file identity key.
 *
 * iOS sometimes reattaches the same FileList to multiple events
 * (change + cancel + polling). Fingerprinting prevents the same
 * File handle from being staged twice within one picker session.
 *
 * The four components — name, size, lastModified, type — are the
 * only stable attributes available on a File. Together they are
 * sufficient to identify "the same user-selected media" across
 * repeated dispatches in one session.
 */
export function fingerprintFile(f) {
  if (!f) return "";
  const name = f.name || "";
  const size = f.size || 0;
  const lm = f.lastModified || 0;
  const type = f.type || "";
  return name + "|" + size + "|" + lm + "|" + type;
}

/**
 * THE contract.
 *
 * @param {HTMLInputElement | null | undefined} input
 *        The file input element to inspect. May be null.
 * @param {Set<string> | undefined} alreadyStaged
 *        Per-picker-session set of file fingerprints that have
 *        already been handed off to the next layer. Mutated in
 *        place — new fingerprints are added so subsequent calls
 *        for the same session won't re-stage.
 *
 * @returns {{
 *   files: File[],               // NEW files (already deduped)
 *   total: number,               // total files in input.files now
 *   newCount: number,            // files.length
 *   alreadyStagedCount: number,  // total - newCount
 *   fingerprints: string[],      // fingerprints of NEW files
 * }}
 *
 * The returned `files` array is a plain Array (NOT a FileList).
 * This is critical: FileList may be invalidated after the JS tick
 * yields. Once acceptPickerHandoff returns, the File handles are
 * safe to use indefinitely.
 */
export function acceptPickerHandoff(input, alreadyStaged) {
  // Defensive null handling — never throws. A missing input is the
  // same as "no files yet"; the caller decides what to do.
  const fileList = input?.files || null;
  const total = fileList ? fileList.length : 0;
  if (total === 0) {
    return { files: [], total: 0, newCount: 0, alreadyStagedCount: 0, fingerprints: [] };
  }
  const dedupSet = alreadyStaged instanceof Set ? alreadyStaged : new Set();
  const newFiles = [];
  const newFingerprints = [];
  // Iterate via index — FileList is array-like, not Array.
  for (let i = 0; i < total; i++) {
    const f = fileList[i];
    if (!f) continue;
    const fp = fingerprintFile(f);
    if (dedupSet.has(fp)) continue;
    dedupSet.add(fp);
    newFiles.push(f);
    newFingerprints.push(fp);
  }
  return {
    files: newFiles,
    total,
    newCount: newFiles.length,
    alreadyStagedCount: total - newFiles.length,
    fingerprints: newFingerprints,
  };
}

/**
 * Returns a fresh, empty per-picker-session dedup set. Call this
 * at the start of every picker session (i.e. inside openPicker()).
 *
 * This pattern — caller owns the Set, contract function reads/
 * mutates it — keeps the contract pure (no module-level state)
 * while still preventing the same FileList from being staged
 * multiple times within one user gesture.
 */
export function newSessionFingerprintSet() {
  return new Set();
}
